import { InfoWindow, useMap } from '@vis.gl/react-google-maps';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type Marker,
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';
import { Client } from '../../Models/Client';
import { ClientMarker } from './ClientMarker';

export type ClusteredClientMarkersProps = {
  clients: Client[];
};

/**
 * The ClusteredTreeMarkers component is responsible for integrating the
 * markers with the markerclusterer.
 */
export const ClusteredClientMarkers = ({
  clients,
}: ClusteredClientMarkersProps) => {
  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});
  const [selectedClientKey, setSelectedClientKey] = useState<string | null>(
    null
  );

  const mcOptions = {
    gridSize: 50,
    minimumClusterSize: 10,
  };

  const selectedClient = useMemo(
    () =>
      clients && selectedClientKey
        ? clients.find((t) => t.id.toString() === selectedClientKey)!
        : null,
    [clients, selectedClientKey]
  );

  // create the markerClusterer once the map is available and update it when
  // the markers are changed
  const map = useMap();

  const clusterer = useMemo(() => {
    if (!map) return null;

    return new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({
        radius: 60,
        extent: 256,
        maxZoom: 17,
      }),
    });
  }, [map]);

  useEffect(() => {
    if (!clusterer) return;

    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers]);

  // this callback will effectively get passsed as ref to the markers to keep
  // tracks of markers currently on the map
  const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
    setMarkers((markers) => {
      if ((marker && markers[key]) || (!marker && !markers[key]))
        return markers;

      if (marker) {
        return { ...markers, [key]: marker };
      } else {
        const { [key]: _, ...newMarkers } = markers;

        return newMarkers;
      }
    });
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setSelectedClientKey(null);
  }, []);

  const handleMarkerClick = useCallback((client: Client) => {
    setSelectedClientKey(client.id.toString());
  }, []);

  return (
    <>
      {clients.map((client) => (
        <ClientMarker
          key={client.id.toString()}
          client={client}
          onClick={handleMarkerClick}
          setMarkerRef={setMarkerRef}
        />
      ))}

      {selectedClientKey && (
        <InfoWindow
          anchor={markers[selectedClientKey]}
          onCloseClick={handleInfoWindowClose}
        >
          {selectedClient && (
            <div className='flex flex-col'>
              <span className='font-bold'>{selectedClient.name}</span>
              <span>
                {selectedClient.city}, {selectedClient.state?.name}{' '}
                {selectedClient.zip}
              </span>
              <span>{selectedClient.phone}</span>
            </div>
          )}
        </InfoWindow>
      )}
    </>
  );
};
